import {
  CheckCircle2, Circle, Clock, User 
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type {
  PurchaseOrder, POStatus 
} from '../../types';
import {
  STATUS_ORDER, type WorkflowStep 
} from './types';

interface TimelineTabProps {readonly po: PurchaseOrder;}

const WORKFLOW_STEPS: WorkflowStep[] = [
  {
    status: 'draft',
    label: 'Draft',
    description: 'PO created and saved as draft' 
  },
  {
    status: 'pending',
    label: 'Pending Approval',
    description: 'Awaiting manager approval' 
  },
  {
    status: 'approved',
    label: 'Approved',
    description: 'Approved by purchasing manager' 
  },
  {
    status: 'sent',
    label: 'Sent to Supplier',
    description: 'PO transmitted to supplier' 
  },
  {
    status: 'acknowledged',
    label: 'Acknowledged',
    description: 'Supplier confirmed receipt' 
  },
  {
    status: 'shipped',
    label: 'Shipped',
    description: 'Order dispatched by supplier' 
  },
  {
    status: 'delivered',
    label: 'Delivered',
    description: 'Goods received at destination' 
  },
];

function getStepStatus(
  currentStatus: POStatus,
  stepStatus: POStatus
): 'completed' | 'current' | 'upcoming' {
  if (currentStatus === 'cancelled') return 'upcoming';
  const currentIndex = STATUS_ORDER.indexOf(currentStatus);
  const stepIndex = STATUS_ORDER.indexOf(stepStatus);
  if (stepIndex < currentIndex) return 'completed';
  if (stepIndex === currentIndex) return 'current';
  return 'upcoming';
}

export function TimelineTab({ po }: TimelineTabProps) {
  return (
    <div className="space-y-6">
      <ApprovalWorkflow po={po} />
      <ActivityLog po={po} />
    </div>
  );
}

function ApprovalWorkflow({ po }: { readonly po: PurchaseOrder }) {
  return (
    <div>
      <h3 className="mb-4 font-medium text-gray-900">Approval Workflow</h3>
      <div className="relative">
        {WORKFLOW_STEPS.map((step, index) => {
          const stepStatus = getStepStatus(po.status, step.status);
          const isLast = index === WORKFLOW_STEPS.length - 1;

          return (
            <div key={step.status} className="relative flex gap-4 pb-8">
              {isLast ? null : (
                <div
                  className={cn(
                    'absolute left-[15px] top-8 h-full w-0.5',
                    stepStatus === 'completed' ? 'bg-green-500' : 'bg-gray-200'
                  )}
                />
              )}
              <div className="relative z-10">
                <StepIcon stepStatus={stepStatus} />
              </div>
              <div className="flex-1 pt-1">
                <p
                  className={cn(
                    'font-medium',
                    stepStatus === 'completed' && 'text-green-700',
                    stepStatus === 'current' && 'text-primary',
                    stepStatus === 'upcoming' && 'text-gray-400'
                  )}
                >
                  {step.label}
                </p>
                <p className="text-sm text-gray-500">{step.description}</p>
                {stepStatus === 'completed' &&
                  step.status === 'approved' &&
                  po.approvedByName && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-gray-400">
                    <User className="h-3 w-3" />
                    {po.approvedByName}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StepIcon({stepStatus,}: {readonly stepStatus: 'completed' | 'current' | 'upcoming';}) {
  if (stepStatus === 'completed') {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500">
        <CheckCircle2 className="h-5 w-5 text-white" />
      </div>
    );
  }
  if (stepStatus === 'current') {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
        <Clock className="h-5 w-5 text-white" />
      </div>
    );
  }
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-200 bg-white">
      <Circle className="h-5 w-5 text-gray-300" />
    </div>
  );
}

function ActivityLog({ po }: { readonly po: PurchaseOrder }) {
  return (
    <div className="rounded-lg border">
      <div className="border-b bg-gray-50 px-4 py-3">
        <h3 className="font-medium text-gray-900">Activity Log</h3>
      </div>
      <div className="divide-y">
        <ActivityLogItem
          action="PO Created"
          user={po.createdByName}
          timestamp={po.createdAt}
        />
        {po.approvedByName && (
          <ActivityLogItem
            action="PO Approved"
            user={po.approvedByName}
            timestamp={po.updatedAt}
          />
        )}
        {po.status === 'shipped' && (
          <ActivityLogItem
            action="Marked as Shipped"
            user="System"
            timestamp={po.updatedAt}
          />
        )}
        {po.status === 'delivered' && po.actualDeliveryDate && (
          <ActivityLogItem
            action="Delivery Confirmed"
            user="Receiving Team"
            timestamp={po.actualDeliveryDate}
          />
        )}
      </div>
    </div>
  );
}

function ActivityLogItem({
  action,
  user,
  timestamp,
}: {
  readonly action: string;
  readonly user: string;
  readonly timestamp: string;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div>
        <p className="font-medium text-gray-900">{action}</p>
        <p className="text-sm text-gray-500">by {user}</p>
      </div>
      <p className="text-sm text-gray-400">
        {new Date(timestamp).toLocaleString()}
      </p>
    </div>
  );
}
